import {
  action,
  computed,
  makeObservable,
  onBecomeObserved,
  override,
} from "mobx"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { RefundsFilter } from "@src/pages/transactionsRefunds/constants"
import { catchErrors, refundsApi } from "@src/shared/api"
import { Pagination, RefundResponse } from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"

export type RefundsLoadRequest = {
  limit?: number
  network?: string
  order?: "asc" | "desc"
  page?: number
  paymentId?: string
  status?: "completed" | "failed" | "pending"
  symbol?: string
}

export class RefundsCollection extends loading(
  ["fetch"],
  CollectionWithPagination<RefundResponse>,
) {
  private _currencies = GlobalCurrenciesCollection()

  constructor() {
    super()

    makeObservable(this, {
      fetch: action.bound,
      networks: computed,
      pagination: override,
      symbols: computed,
    })

    onBecomeObserved(this, "isLoading", this.load)
  }

  async fetch(req: RefundsLoadRequest = {}) {
    try {
      const { page = 0 } = req

      const {
        data: { data },
      } = await refundsApi.listRefunds(
        req.order,
        this.calcOffset(page),
        this.pageSize,
        req.paymentId,
        req.network,
        req.symbol,
        req.status,
      )

      if (data) {
        return {
          data: data.data as RefundResponse[],
          pagination: data.pagination as Pagination,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }

  get filter() {
    return new CollectionFilters<any>(
      [
        ...RefundsFilter,
        CollectionFilters.createField("network").select(this.networks, {
          col: 6,
          label: "Network",
          placeholder: "Select a network",
        }),

        CollectionFilters.createField("symbol").select(this.symbols, {
          col: 6,
          label: "Symbol",
          placeholder: "Select a symbol",
        }),
      ],
      this.load,
    )
  }

  get networks() {
    return this._currencies.cryptoNetworks.map((i) => ({
      label: i,
      value: i,
    }))
  }

  get symbols() {
    return [...new Set(this._currencies.crypto.map((i) => i.symbol))].map(
      (i) => ({ label: i, value: i }),
    )
  }
}
